import soundSilence from '@wetransfer/transfer/public/assets/audio/silence.mp3';
import soundAntLadder from '@wetransfer/transfer/public/assets/audio/upload-complete-notifications/ant-ladder.mp3';
import soundArrive from '@wetransfer/transfer/public/assets/audio/upload-complete-notifications/arrive.mp3';
import soundComb from '@wetransfer/transfer/public/assets/audio/upload-complete-notifications/comb.mp3';
import soundForYou from '@wetransfer/transfer/public/assets/audio/upload-complete-notifications/for-you.mp3';
import soundJoyful from '@wetransfer/transfer/public/assets/audio/upload-complete-notifications/joyful.mp3';
import soundPotato from '@wetransfer/transfer/public/assets/audio/upload-complete-notifications/potato.mp3';
import soundSmirk from '@wetransfer/transfer/public/assets/audio/upload-complete-notifications/smirk.mp3';
import soundWelcome from '@wetransfer/transfer/public/assets/audio/upload-complete-notifications/welcome.mp3';

export const UploadNotificationLocalStorageKeys = {
  ACTIVE: 'uploadNotificationActive',
  SOUND: 'uploadNotificationSound',
};

export const uploadNotificationSounds = {
  default: soundComb,
  'ant-ladder': soundAntLadder,
  smirk: soundSmirk,
  arrive: soundArrive,
  welcome: soundWelcome,
  'for-you': soundForYou,
  joyful: soundJoyful,
  potato: soundPotato,
} as const;

export interface UploadNotificationState {
  active: boolean;
  sound: UploadNotificationSound;
}

export type UploadNotificationSound = keyof typeof uploadNotificationSounds;

export const requestNotificationPermission = (): void => {
  if (
    typeof Notification !== 'undefined' &&
    Notification.permission !== 'granted'
  ) {
    Notification.requestPermission();
  }
};

/**
 * We use a single audio element for all notifications
 * This makes audio play on mobile devices also, where audio
 * can only be played in response to a user interaction
 */
let notificationAudio: HTMLAudioElement | undefined;

export const getNotificationAudio = (src: string): HTMLAudioElement => {
  if (!notificationAudio) {
    notificationAudio = new Audio(soundSilence);
  }

  notificationAudio.src = src || soundSilence;

  return notificationAudio;
};

/**
 * We use this flag to determine if the window is blurred
 * (i.e. the user is not looking at this tab actively, another window is in focus)
 */
let isWindowBlurred = false;

if (typeof window !== 'undefined') {
  window.addEventListener('blur', () => {
    isWindowBlurred = true;
  });
  window.addEventListener('focus', () => {
    isWindowBlurred = false;
  });
}

export const getCanUseNotificationSound = (): boolean => {
  // Firefox forces its own notification sound, so we disable ours
  if (typeof window === 'undefined') return false;
  if (window.navigator?.userAgent?.includes?.('Firefox')) return false;

  return true;
};

export const playUploadSuccessSound = (
  noise: UploadNotificationSound
): void => {
  getNotificationAudio(
    uploadNotificationSounds[noise] || uploadNotificationSounds['default']
  ).play();
};

export const notifyUploadSuccess = (sound: UploadNotificationSound): void => {
  if (getCanUseNotificationSound()) playUploadSuccessSound(sound);

  // Only send desktop notification if this tab isn't in view
  if (
    typeof Notification !== 'undefined' &&
    // If we are not watching this window, or the user cannot use an audio notification (firefox)
    // we send desktop notifications
    (isWindowBlurred || !getCanUseNotificationSound())
  ) {
    const notification = new Notification('Upload complete', {
      body: 'Your transfer has finished uploading.',
      silent: true, // audio is handled above
    });

    notification.onclick = () => {
      window.focus();
    };
  }
};

/**
 * Initiator function for setting up audio playback permissions
 */
export const initNotifications = (): void => {
  // Play empty sound permission which enables audio without user interaction
  window.addEventListener(
    'pointerdown',
    () => {
      getNotificationAudio(soundSilence).play();
    },
    {
      once: true,
    }
  );
};
